import { white } from '@kmns-internal/furikake'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  ReportStatusEnum,
  type ScheduleListInRangeItem,
  type ScheduleListInRangeItemInfo,
  ScheduleTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { addDays, endOfDay } from 'date-fns'
import { type FC, useEffect, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import {
  flattenNodes,
  mergedPlaceNodeIds,
} from 'src/exShared/util/place/placeNode'
import PlaceFilterDropDown from 'src/features/dashboard/PlaceFilterDropDown'
import StatusFilterDropDown from 'src/features/dashboard/StatusFilterDropDown'
import type { RootState } from 'src/state/store'
import date from 'src/util/date'
import {
  useColumnsState,
  useCurrentEndDateState,
  useCurrentStartDateState,
  useFilteredPlaceGroupNodeIdsState,
  useFilteredPlaceNodeIdsState,
  useGetDevianceCountColumns,
  useGetScheduleStatusColumns,
  useHasPresentDayState,
  useIsFilteredState,
  useRequestScheduleData,
} from './shared.hooks'
import {
  ScheduleStatus,
  type TColumnValue,
  type TColumnValuesByDayOfWeek,
  type TDashboardDataType,
  type TDayOfWeekIn3Letters,
  type TItem,
} from './shared.types'

const daysOfWeekIn3Letters: TDayOfWeekIn3Letters[] = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
]
type TColumnNumberForDevianceCount = 0 | 1 | 2 | 3 | 4 | 5 | 6

const isDayOfWeekNumber = (x: number): x is TColumnNumberForDevianceCount =>
  x >= 0 || x <= 6
const convertNumberToDayOfWeekIn3Letters = (n: TColumnNumberForDevianceCount) =>
  daysOfWeekIn3Letters[n]

export const makeDefaultValuesForItem = (type: TDashboardDataType) =>
  daysOfWeekIn3Letters.reduce<TColumnValuesByDayOfWeek>(
    (carrier, dow) => ({
      ...carrier,
      [dow]: {
        // scheduleStatus が null の場合は ScheduleStatus.DEFAULT の status として扱われる
        // ScheduleStatus.DEFAULT をセットしないのは、後続の処理で「記録中がある場合」と「スケジュールがない場合」を分ける必要があるため。
        // null の場合は 「スケジュールがない場合」 として扱われる
        scheduleStatus: null,
        devianceCount: type === 'schedule' ? null : 0,
      },
    }),
    {} as TColumnValuesByDayOfWeek,
  )

export const getMergedScheduleStatus = (
  prevStatus: ScheduleStatus,
  newStatus: ScheduleStatus,
) => {
  if (
    prevStatus === ScheduleStatus.INCOMPLETE ||
    newStatus === ScheduleStatus.INCOMPLETE
  ) {
    return ScheduleStatus.INCOMPLETE
  }

  if (prevStatus === ScheduleStatus.DEFAULT) {
    return ScheduleStatus.DEFAULT
  }

  return newStatus
}

export const isScheduleCompleted = (
  info: ScheduleListInRangeItemInfo,
): boolean =>
  !!info.report?.uuid &&
  info.report?.status?.status !== ReportStatusEnum.INCOMPLETE &&
  info.report?.status?.status !== ReportStatusEnum.APPROVAL_REMAND

/** 日にちスケジュールの当日(今日)のスケジュールで、スケジュール終了時刻を経過していない */
export const isTodaysScheduleNotDueYet = (
  info: ScheduleListInRangeItemInfo,
): boolean =>
  !!(
    info.scheduleType === ScheduleTypeEnum.Date &&
    info.scheduleInDate?.targetDate &&
    date.isToday(info.scheduleInDate.targetDate) &&
    info.scheduleInDate.endTime &&
    date.isAfterNow(info.scheduleInDate.endTime)
  )

/** 日にちスケジュールで当日以降のスケジュールは「-」にする */
export const isFutureDateSchedule = (
  info: ScheduleListInRangeItemInfo,
): boolean =>
  !!(
    info.scheduleType === ScheduleTypeEnum.Date &&
    info.scheduleInDate?.targetDate &&
    date.isAfterNow(info.scheduleInDate.targetDate)
  )

/** 期間スケジュールで、期間終了日を経過していない */
export const isDurationScheduleNotDueYet = (
  info: ScheduleListInRangeItemInfo,
): boolean =>
  !!(
    info.scheduleType === ScheduleTypeEnum.Duration &&
    info.scheduleInDuration?.through &&
    date.isAfterNow(endOfDay(info.scheduleInDuration.through))
  )

/** レポート開始日が対象日付よりも後の日付なら、スケジュール開始日以前のスケジュールは「-」にする */
export const isDateScheduleStartReportDateAfterDueDate = (
  info: ScheduleListInRangeItemInfo,
): boolean =>
  !!(
    info.scheduleType === ScheduleTypeEnum.Date &&
    info.startReportDate &&
    info.scheduleInDate?.targetDate &&
    date.isAfter(info.startReportDate, info.scheduleInDate.targetDate) &&
    !date.isSameDay(info.startReportDate, info.scheduleInDate.targetDate)
  )

/** レポート開始日が対象日付よりも後の日付なら、スケジュール開始日以前のスケジュールは「-」にする */
export const isDurationScheduleStartReportDateAfterDueDate = (
  info: ScheduleListInRangeItemInfo,
): boolean =>
  !!(
    info.scheduleType === ScheduleTypeEnum.Duration &&
    info.startReportDate &&
    info.scheduleInDuration?.through &&
    date.isAfter(info.startReportDate, info.scheduleInDuration.through)
  )

const getScheduleStatus = (
  info: ScheduleListInRangeItemInfo,
): ScheduleStatus => {
  switch (true) {
    case isScheduleCompleted(info):
      return ScheduleStatus.COMPLETED
    case isTodaysScheduleNotDueYet(info):
    case isFutureDateSchedule(info):
    case isDurationScheduleNotDueYet(info):
    case isDateScheduleStartReportDateAfterDueDate(info):
    case isDurationScheduleStartReportDateAfterDueDate(info):
      return ScheduleStatus.DEFAULT
    default:
      return ScheduleStatus.INCOMPLETE
  }
}

const isDayOfWeekLowerCase = (x: string): x is TDayOfWeekIn3Letters =>
  ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].includes(x)

const convertToScheduleStatusItem = (
  items: ScheduleListInRangeItem[],
): TItem[] => {
  const result: { [placeNodeUUID: string]: TItem } = {}
  items.forEach(({ targetDate, infos }) => {
    if (!targetDate || !infos || !infos.length) {
      return
    }
    const dayOfTheWeekEng = date
      .formatISODayOfWeek(new Date(targetDate))
      .toLocaleLowerCase()

    infos.forEach(info => {
      const placeNode = info.placeNode
      const placeNodeUUID = placeNode?.uuid ?? ''

      if (!(placeNodeUUID in result)) {
        // 同一現場で 1 回もステータス判定されていないとき
        result[placeNodeUUID] = {
          ...makeDefaultValuesForItem('schedule'),
          key: placeNodeUUID,
          place: { uuid: placeNodeUUID, name: placeNode?.place?.name ?? '' },
        }
      }

      if (!isDayOfWeekLowerCase(dayOfTheWeekEng)) {
        return
      }

      const prevStatus =
        result[placeNodeUUID]?.[dayOfTheWeekEng].scheduleStatus ??
        ScheduleStatus.COMPLETED

      // 期間スケジュールの場合の表示条件
      // 1. 最終日はアイコンに反映する
      // 2. 最終日以外はアイコンに反映しない
      //    ただし、スケジュールに表示は必要なため、
      //    他に表示すべきスケジュールがないときは "-" を表示する
      if (info.scheduleType === ScheduleTypeEnum.Duration) {
        if (
          date.formatYYYYMMDD_hyphen_locale(
            info.scheduleInDuration?.through,
          ) !== targetDate
        ) {
          return
        }
      }

      const newStatus = getScheduleStatus(info)
      const mergedScheduleStatus = getMergedScheduleStatus(
        prevStatus,
        newStatus,
      )

      const placeResult = result[placeNodeUUID]
      if (placeResult) {
        placeResult[dayOfTheWeekEng].scheduleStatus = mergedScheduleStatus
      }
    })
  })
  return Object.values(result)
}

const convertToDevianceCountItem = (items: ScheduleListInRangeItem[]) => {
  const devianceCountItems: TItem[] = []
  items.forEach(({ infos }, index) => {
    if (isDayOfWeekNumber(index)) {
      const dayOfWeek = convertNumberToDayOfWeekIn3Letters(index)

      infos?.forEach(({ placeNode, report }) => {
        if (devianceCountItems.some(val => val?.key === placeNode?.uuid)) {
          const targetItem = devianceCountItems.find(
            val => val?.place.uuid === placeNode?.uuid,
          )

          if (!targetItem) return

          const addcount = report?.invalidCount ?? 0

          if (dayOfWeek) {
            targetItem[dayOfWeek].devianceCount =
              (targetItem[dayOfWeek].devianceCount ?? 0) + addcount
          }
        } else {
          const firstCount = report?.invalidCount ?? 0
          const pushCount = devianceCountItems.push({
            ...makeDefaultValuesForItem('deviance'),
            key: placeNode?.uuid ?? '',
            place: {
              name: placeNode?.place?.name ?? '',
              uuid: placeNode?.uuid ?? '',
            },
          })

          // countItemsの要素数(pushCount) - 1のインデックスに異常数が記録されている
          const countItem = devianceCountItems[pushCount - 1]
          if (countItem && dayOfWeek) {
            countItem[dayOfWeek].devianceCount = firstCount
          }
        }
      })
    }
  })

  return devianceCountItems
}

// 年月表示部分を生成する処理 (always: endDate - startDate = 6)
const renderMonth = (startDate: Date) => {
  const label = date.formatYYYYM_JP(startDate)
  return <Typography variant="h5">{label}</Typography>
}

const isIncomplete = (scheduleStatus: ScheduleStatus | null) =>
  scheduleStatus === ScheduleStatus.INCOMPLETE
export const filterScheduleItems = (data: TItem[]): TItem[] => {
  const firstMonday = data[0]?.mon

  if (firstMonday) {
    return data.filter(item =>
      daysOfWeekIn3Letters.some(d =>
        firstMonday.scheduleStatus !== null
          ? isIncomplete(item[d].scheduleStatus)
          : firstMonday.devianceCount !== null
            ? !!item[d].devianceCount
            : undefined,
      ),
    )
  }

  return []
}

interface IProps {
  type: TDashboardDataType
}

const DashboardStatusTable: FC<IProps> = ({ type }) => {
  const isSchedule = type === 'schedule'

  const placeNodes = useSelector(
    (state: RootState) => flattenNodes(state.placesState.placeGroups.nodes),
    shallowEqual,
  )
  const isLoading = useSelector(
    ({
      scheduleListInRangeItemsState: {
        scheduleListInRangeItems: schedule,
        invalidCountScheduleListInRangeItems: deviance,
      },
    }: RootState) => (isSchedule ? schedule : deviance).isLoading,
  )
  const schedules = useSelector(
    ({
      scheduleListInRangeItemsState: {
        scheduleListInRangeItems: { scheduleListInRangeItems },
        invalidCountScheduleListInRangeItems: {
          invalidCountScheduleListInRangeItems,
        },
      },
    }: RootState) =>
      isSchedule
        ? scheduleListInRangeItems
        : invalidCountScheduleListInRangeItems,
    shallowEqual,
  )

  const requestScheduleData = useRequestScheduleData()

  const [startDate, setCurrentStartDate] = useCurrentStartDateState(type)
  const [endDate, setCurrentEndDate] = useCurrentEndDateState(type)
  const [hasPresentDay, setHasPresentDay] = useHasPresentDayState(type)
  const [columns, setColumns] = useColumnsState(type)
  const [filteredPlaceNodeIds] = useFilteredPlaceNodeIdsState(type)
  const [filteredPlaceGroupNodeIds] = useFilteredPlaceGroupNodeIdsState(type)
  const [isFiltered] = useIsFilteredState(type)

  const [scheduleItems, setScheduleItems] = useState<TItem[] | undefined>()

  const getScheduleStatusColumns = useGetScheduleStatusColumns()
  const getDevianceCountColumns = useGetDevianceCountColumns()

  useEffect(() => {
    const convertedDataSource = (
      isSchedule ? convertToScheduleStatusItem : convertToDevianceCountItem
    )(schedules)

    // eslint-disable-next-line react-hooks/set-state-in-effect -- FIXME: eslint-plugin-react-hooks が 7.0.1 にバージョンアップした際にエラーになるようになったため一旦無視。ここのコードを触る際に修正してください🙏
    setScheduleItems(
      isFiltered
        ? filterScheduleItems(convertedDataSource)
        : convertedDataSource,
    )
  }, [isFiltered, isSchedule, schedules])

  const getColumns = isSchedule
    ? getScheduleStatusColumns
    : getDevianceCountColumns

  const onWeekClick = (type: 'nextWeek' | 'lastWeek') => {
    const isNextWeek = type === 'nextWeek'

    if (isLoading || (hasPresentDay && isNextWeek)) {
      return
    }

    const addDayNumber: number = isNextWeek ? 7 : -7

    const newStartDate = startDate
      ? addDays(startDate, addDayNumber)
      : undefined
    const newEndDate = endDate ? addDays(endDate, addDayNumber) : undefined

    if (!newStartDate || !newEndDate) {
      return
    }

    const isPresentDay =
      newStartDate && newEndDate
        ? date.isWithinRange(date.today(), newStartDate, endOfDay(newEndDate))
        : false

    setCurrentStartDate(newStartDate)
    setCurrentEndDate(newEndDate)
    setHasPresentDay(isPresentDay)
    setColumns(getColumns(newStartDate, newEndDate))

    if (!newStartDate || !newEndDate) {
      return
    }
    const placeNodeIds = mergedPlaceNodeIds(
      placeNodes,
      filteredPlaceNodeIds,
      filteredPlaceGroupNodeIds,
    )
    requestScheduleData(isSchedule, placeNodeIds, newStartDate, newEndDate)
  }

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        sx={{ p: 2 }}
        justifyContent="flex-start"
        alignItems="center"
      >
        {startDate && endDate && renderMonth(startDate)}
        <Button
          variant="text"
          size="medium"
          startIcon={<ChevronLeft />}
          disabled={isLoading}
          onClick={() => onWeekClick('lastWeek')}
        >
          前の週
        </Button>
        <Button
          variant="text"
          size="medium"
          endIcon={<ChevronRight />}
          disabled={isLoading || hasPresentDay}
          onClick={() => onWeekClick('nextWeek')}
        >
          次の週
        </Button>
        <PlaceFilterDropDown isSchedule={isSchedule} />
        <StatusFilterDropDown isSchedule={isSchedule} />
      </Stack>

      <Box position="relative">
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <CircularProgress data-testid="dashboard-loading" />
          </Box>
        )}
        <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
          <Table
            sx={{
              backgroundColor: white,
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell
                    key={col.dataIndex}
                    align={col.align}
                    sx={{ width: col.width, padding: 1 }}
                  >
                    {col.title()}
                  </TableCell>
                ))}
                <TableCell key={`empty-header`}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ p: 2 }}>
              {scheduleItems?.map((row, rowIndex) => (
                <TableRow key={row.key}>
                  {columns.map(col => (
                    <TableCell
                      key={col.dataIndex}
                      align={col.align}
                      sx={{ width: col.width, padding: 2 }}
                    >
                      {col.render(
                        row[col.dataIndex as keyof TItem] as TColumnValue,
                        row,
                      )}
                    </TableCell>
                  ))}

                  <TableCell key={`empty-${rowIndex}`}></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  )
}

export default DashboardStatusTable
